import React, { useEffect, useRef, useState } from 'react';
import { MapPin, Loader2 } from 'lucide-react';
import { Input } from './ui/input';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

export default function AddressAutocomplete({ 
  onAddressSelect, 
  defaultValue = '',
  placeholder = 'Start typing your address...',
  className = ''
}) {
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const [inputValue, setInputValue] = useState(defaultValue);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Load Google Maps script
  useEffect(() => {
    // Check if already loaded with places library
    if (window.google?.maps?.places?.Autocomplete) {
      setIsLoaded(true);
      setIsLoading(false);
      return;
    }

    // If google.maps exists but places is not loaded, we need to add the places library
    // This happens when another component loaded Maps without places
    const checkInterval = setInterval(() => {
      if (window.google?.maps?.places?.Autocomplete) {
        setIsLoaded(true);
        setIsLoading(false);
        clearInterval(checkInterval);
      }
    }, 500);

    // Check if script is already being loaded
    const existingScript = document.querySelector('script[src*="maps.googleapis.com/maps/api/js"]');
    if (existingScript) {
      // Wait for it to load
      if (existingScript.complete || existingScript.readyState === 'complete') {
        if (window.google?.maps?.places?.Autocomplete) {
          setIsLoaded(true);
          setIsLoading(false);
          clearInterval(checkInterval);
        }
      } else {
        existingScript.addEventListener('load', () => {
          setTimeout(() => {
            if (window.google?.maps?.places?.Autocomplete) {
              setIsLoaded(true);
              setIsLoading(false);
              clearInterval(checkInterval);
            }
          }, 100);
        });
      }
      return () => clearInterval(checkInterval);
    }

    // Load the script
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      setTimeout(() => {
        setIsLoaded(true);
        setIsLoading(false);
        clearInterval(checkInterval);
      }, 100);
    };
    script.onerror = () => {
      console.error('Failed to load Google Maps');
      setIsLoading(false);
      clearInterval(checkInterval);
    };
    document.head.appendChild(script);

    return () => {
      clearInterval(checkInterval);
    };
  }, []);

  // Initialize autocomplete
  useEffect(() => {
    if (!isLoaded || !inputRef.current || autocompleteRef.current) return;

    try {
      autocompleteRef.current = new window.google.maps.places.Autocomplete(inputRef.current, {
        componentRestrictions: { country: 'us' },
        fields: ['address_components', 'formatted_address', 'geometry'],
        types: ['address']
      });

      autocompleteRef.current.addListener('place_changed', () => {
        const place = autocompleteRef.current.getPlace();
        
        if (!place.address_components) {
          return;
        }

        // Parse address components
        const addressComponents = {
          street_number: '',
          route: '',
          city: '',
          state: '',
          zip_code: '',
          formatted_address: place.formatted_address || ''
        };

        for (const component of place.address_components) {
          const type = component.types[0];
          switch (type) {
            case 'street_number':
              addressComponents.street_number = component.long_name;
              break;
            case 'route':
              addressComponents.route = component.long_name;
              break;
            case 'locality':
            case 'sublocality_level_1':
              addressComponents.city = component.long_name;
              break;
            case 'administrative_area_level_1':
              addressComponents.state = component.short_name;
              break;
            case 'postal_code':
              addressComponents.zip_code = component.long_name;
              break;
            default:
              break;
          }
        }

        // Combine street number and route
        const street = `${addressComponents.street_number} ${addressComponents.route}`.trim();
        
        // Update input value
        setInputValue(place.formatted_address || street);

        // Call the callback with parsed address
        if (onAddressSelect) {
          onAddressSelect({
            street,
            city: addressComponents.city,
            state: addressComponents.state,
            zip_code: addressComponents.zip_code,
            formatted_address: addressComponents.formatted_address,
            lat: place.geometry?.location?.lat(),
            lng: place.geometry?.location?.lng()
          });
        }
      });
    } catch (error) {
      console.error('Error initializing autocomplete:', error);
    }
  }, [isLoaded, onAddressSelect]);

  // Handle manual input changes
  const handleInputChange = (e) => {
    setInputValue(e.target.value);
  };

  return (
    <div className={`relative ${className}`}>
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 pointer-events-none z-10" />
        <Input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          placeholder={isLoading ? 'Loading...' : placeholder}
          className="pl-10 pr-10 h-12"
          disabled={isLoading}
          data-testid="address-autocomplete-input"
        />
        {isLoading && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 animate-spin" />
        )}
      </div>
      {isLoaded && (
        <p className="text-xs text-slate-400 mt-1">Start typing to see address suggestions</p>
      )}
    </div>
  );
}
